import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/auth';

const ParentDashboard = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Enfants
      try {
        const res = await api.get('/parent/children', { headers });
        const list = Array.isArray(res.data) ? res.data : (res.data.children || res.data.data || []);
        setChildren(list);
      } catch (e) {
        setChildren([]);
      }

      // Derniers bulletins (via le premier enfant)
      try {
        const res = await api.get('/parent/children', { headers });
        const list = Array.isArray(res.data) ? res.data : (res.data.children || res.data.data || []);
        if (list.length > 0) {
          const firstChildId = list[0].id;
          const rc = await api.get(`/parent/children/${firstChildId}/report-cards`, { headers });
          setBulletins(Array.isArray(rc.data) ? rc.data.slice(0, 3) : []);
        }
      } catch (e) {
        setBulletins([]);
      }

      // Prochaines absences
      try {
        const res = await api.get('/parent/children', { headers });
        const list = Array.isArray(res.data) ? res.data : (res.data.children || res.data.data || []);
        const allAbsences = [];
        for (const child of list.slice(0, 2)) {
          try {
            const a = await api.get(`/parent/children/${child.id}/absences`, { headers });
            const childAbsences = (Array.isArray(a.data) ? a.data : []).slice(0, 2).map(abs => ({
              ...abs,
              child_name: child.name,
              child_class: child.class_name,
            }));
            allAbsences.push(...childAbsences);
          } catch (e) {}
        }
        setAbsences(allAbsences.slice(0, 3));
      } catch (e) {
        setAbsences([]);
      }
    } catch (err) {
      console.error('Erreur dashboard parent:', err);
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.name
    ? user.name.split(' ').slice(-1)[0].charAt(0).toUpperCase() + user.name.split(' ').slice(-1)[0].slice(1).toLowerCase()
    : 'Parent';

  // Couleurs par enfant (rotation)
  const colors = ['bg-[#3B82F6]', 'bg-[#22C55E]', 'bg-[#A855F7]', 'bg-[#F39C12]'];

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">

      {/* ═══════════ BANNIÈRE HAUT ═══════════ */}
      <div className="relative rounded-2xl overflow-hidden mb-6 h-48 lg:h-56 bg-gradient-to-r from-[#0A0A0A] to-[#F39C12]">
        <img
          src="/images/banner-parent.png"
          alt="Parent"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent"></div>

        <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-center max-w-xl">
          <h1 className="text-white text-3xl lg:text-4xl font-bold">
            Bonjour {firstName} 👋
          </h1>
          <p className="text-white/95 text-base lg:text-lg mt-3">
            Suivez la scolarité de vos enfants<br />en toute simplicité.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">Chargement...</div>
      ) : children.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <p className="text-5xl mb-4">📭</p>
          <p className="text-lg font-bold text-[#0A0A0A] mb-2">Aucun enfant enregistré</p>
          <p className="text-gray-500 text-sm">
            Contactez l'administration pour associer vos enfants à votre compte.
          </p>
        </div>
      ) : (
        <>
          {/* ═══════════ MES ENFANTS (3 cartes) ═══════════ */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {children.slice(0, 3).map((child, idx) => (
              <Link
                key={child.id}
                to={`/parent/children/${child.id}/grades`}
                className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg hover:border-[#F39C12]/30 transition group"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-14 h-14 rounded-full ${colors[idx % colors.length]} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
                    {child.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '👦'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#0A0A0A] truncate">{child.name}</p>
                    <p className="text-xs text-gray-500">{child.class_name || 'Classe non assignée'}</p>
                  </div>
                  <span className="text-gray-400 group-hover:text-[#F39C12] group-hover:translate-x-1 transition">
                    →
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* ═══════════ 2 COLONNES : BULLETINS + ABSENCES ═══════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

            {/* Derniers bulletins */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Derniers bulletins</h3>
                <Link
                  to="/parent/bulletins"
                  className="text-[#F39C12] hover:text-[#E67E22] text-sm font-medium"
                >
                  Voir tous →
                </Link>
              </div>

              {bulletins.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-3xl mb-2">📊</p>
                  <p className="text-gray-500 text-sm">Aucun bulletin disponible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bulletins.map((b) => (
                    <div key={b.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-[#FFF3E0] transition">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#F39C12]/15 flex items-center justify-center flex-shrink-0">
                          📄
                        </div>
                        <div>
                          <p className="font-bold text-sm text-[#0A0A0A]">
                            {b.student_name || 'Élève'} - {b.period_label || b.period}
                          </p>
                          <p className="text-xs text-gray-500">
                            {b.academic_year || '2026-2027'}
                          </p>
                        </div>
                      </div>
                      <button className="text-xs bg-[#F39C12] text-white px-3 py-1.5 rounded-lg hover:bg-[#E67E22] transition font-medium whitespace-nowrap">
                        Télécharger
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Prochaines absences */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Prochaines absences</h3>
                <Link
                  to="/parent/absences"
                  className="text-[#F39C12] hover:text-[#E67E22] text-sm font-medium"
                >
                  Voir tout →
                </Link>
              </div>

              {absences.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-3xl mb-2">📅</p>
                  <p className="text-gray-500 text-sm">Aucune absence enregistrée</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {absences.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-[#FFF3E0] transition">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                        🕐
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-[#0A0A0A]">
                          {a.child_name} - {a.child_class}
                        </p>
                        <p className="text-xs text-gray-500">{a.date} • {a.reason || 'Sans motif'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ═══════════ BANNIÈRE BAS ═══════════ */}
          <div className="bg-orange-50 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#F39C12] flex items-center justify-center flex-shrink-0 text-white text-2xl">
              💛
            </div>
            <div>
              <p className="font-bold text-[#0A0A0A] text-base">Ensemble pour la réussite de vos enfants</p>
              <p className="text-gray-600 text-sm">
                Votre implication fait la différence dans leur parcours scolaire.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ParentDashboard;